import * as React from "react";

import "./kit-options-step.scss";

import {isLegacyItem}                  from "@Common/logic/item/is-legacy-item";
import {isPickPack}                    from "@Common/logic/item/is-pick-pack";
import {cs}                            from "@Common/react/chain-services";
import {consumeContext}                from "@Common/react/context";
import {keyed}                         from "@Common/react/keyed";
import {Load2}                         from "@Common/react/load2";
import {scopedState}                   from "@Common/react/scoped-state";
import {UseState}                      from "@Common/react/use-state";
import {InformationBadge}              from "@Common/ui-components/heavy/common/design2/information-badge/information-badge";
import {ItemOptions}                   from "@Common/ui-components/heavy/item-options/item-options";
import {buildUrlQuery}                 from "@Common/utils/build-url-query";
import {equalDeep, oMapToArr}          from "@Common/utils/objects";
import {parseUrlQuery}                 from "@Common/utils/parse-url-query";

import {EnsureItemAddedToCart}         from "@Xpresso/routes/common/logic/cart-logic/ensure-item-added-to-cart";

import {PageShow}                      from "../../../common/components/page-show";
import {ProductCustomizeStatus}        from "../../../common/product-customize-status/product-customize-status";
import {CustomizeLayout}               from "../common/customize-layout/customize-layout";
import {EmulateAgentOfOrder}           from "../product-options/loaders/emulate-agent-of-order";
import {CheckoutButton}                from "./checkout-button/checkout-button";
import {loadItem}                      from "./loaders/load-item";
import {loadTemplateBack}              from "./loaders/load-template-back";
import {loadTemplateOptions}           from "./loaders/load-template-options";
import {pickPackModules}               from "./modules/pick-pack/pick-pack-modules";
import {yardSignModules}               from "./modules/yard-sign/yard-sign-modules";
import {ComponentEditor}               from "./component-editor/component-editor";
import {RedirectAction}                from "./redirect-action/redirect-action";
import {TemplateSelectionTemplateView} from "./template-selection-template-view/template-selection-template-view";

export const KitOptionsStep = ({match, location}) => cs(
    ["query", (_, next) => next(parseUrlQuery(location.search))],
    consumeContext("apis"),
    consumeContext("company"),
    consumeContext("routerHistory"),
    consumeContext("systemConstants"),
    ["itemId", (_, next) => next(match.params.item)],
    ["item", ({itemId}, next) => itemId ? (
        loadItem({itemId, next})
    ) : (
        next()
    )],
    ({item}, next) => item ? (
        EmulateAgentOfOrder({item, next})
    ) : (
        next()
    ),
    ["savedState", ({item}, next) => item && UseState({
        initValue: item,
        next
    })],
    ["itemState", ({item}, next) => item && UseState({
        initValue: item,
        next
    })],
    ["itemComponentState", ({itemState}, next) => next(scopedState(itemState, ["components"] ))],
    ["masterTemplateOptions", ({item}, next) => loadTemplateOptions({
        templateId: item?.sku?.front,
        templateBackId: item?.sku?.back,
        fullPageLoading: true,
        next,
    })],
    ({query, item}, next) => CustomizeLayout({
        active: "kit-options",
        back: {
            link: (`/product-options/order/${item.itemId}` + buildUrlQuery({
                ...query?.referrer === "approval-requests" && {
                    id_cart: query?.id_cart,
                    referrer: "approval-requests"
                }
            })),
            label: 'Back to Product Options',
        },
        addressable: item.addressable,
        customizable: !(item.source === 'OIA') && !isPickPack(item),
        item,
        isKit: item?.components,
        disabled: ["checkout", "customization", "distribution-options", "kit-options"],
        next,
    }),
    ["customizeStatus", ({pageShow}, next) => ProductCustomizeStatus({pageShow, next})],
    ["ensureItemAddedToCart", (_, next) => EnsureItemAddedToCart({next})],
    ["redirectAction", (
        {
            apis,
            company,
            customizeStatus,
            ensureItemAddedToCart,
            item,
            itemState,
            query,
            routerHistory,
            savedState,
            systemConstants,
        },
        next
    ) => RedirectAction({
        apis,
        companyAllowBuild3: company.prefs.allowBuild3,
        ensureItemAddedToCart,
        status: customizeStatus,
        item,
        itemState,
        isFromApproval: query?.referrer === "approval-requests",
        routerHistory,
        savedState,
        systemConstants,
        next,
    })],
    ["componentTemplateId", ({
        redirectAction,
        customizeStatus,
        itemComponentState,
    }, next) => (
        <div className="kit-option-step-h8f kit-option-step">
            {!customizeStatus.active ? (<>
                <div className="header-section">
                    <div className="title-section">
                        <div className="title" xd-qa-type="label" xd-qa-name="Kit Review">
                            Kit Review
                        </div>
                        <div className="sub-title">
                            Review the kit items before checking out
                        </div>
                    </div>
                    <div className="button">
                        {CheckoutButton({
                            redirectAction,
                            disabled: itemRequiresAttention(itemComponentState.value),
                        })}
                    </div>
                </div>
                <hr className="separator"/>
                {itemRequiresAttention(itemComponentState.value) ? (
                    <div className="attention-required">
                        <div className="title" xd-qa-type="label" xd-qa-name="Required">
                            The following kit items require your attention {InformationBadge({
                                height: 20,
                                getTooltip: () => "There are required fields that need to be handled before continuing",
                                xdQaName: "Required Fields"
                            })}
                        </div>
                        {oMapToArr(itemComponentState.value,(component, i) => cs(
                            keyed(i),
                            () => (componentRequiresAttention(component)
                            )
                                && next(i)
                        ), "sequenceNumber")}
                    </div>
                ) : (
                    <div className="title" xd-qa-type="label" xd-qa-name="Complete">
                        All kit items have been customized. Proceed to Checkout when you’re ready.
                    </div>
                )}
                <div className="item-ready">
                    {oMapToArr(itemComponentState.value,(component, i) => cs(
                        keyed(i),
                        () => (!componentRequiresAttention(component)
                        )
                            && next(i)
                    ), "sequenceNumber")}
                </div>
                <div className="button">
                    {CheckoutButton({
                        redirectAction,
                        disabled: itemRequiresAttention(itemComponentState.value),
                    })}
                </div>
            </>) : (
                <div className="center">
                    {customizeStatus.message}
                </div>
            )}
        </div>
    )],
    ["loadingProofs", (_, next) => UseState({
        initValue: true,
        next
    })],
    ["componentState", ({componentTemplateId, itemComponentState}, next) => next(
        scopedState(itemComponentState, [componentTemplateId])
    )],
    ["componentBack", ({componentState}, next) => loadTemplateBack({templateId: componentState?.value?.sku?.front ?? 0, next, fullPageLoading: false})],
    ["pageShow", (_, next) => <PageShow{...{next}}/>],

    ["componentTemplateOptions", ({componentState}, next) => loadTemplateOptions({
        templateId: componentState?.value?.sku?.front,
        templateBackId: componentState?.value?.sku?.back,
        fullPageLoading: false,
        next,
    })],
    ["componentProofingStatus", ({apis, componentState, componentTemplateOptions, componentTemplateId, loadingProofs, masterTemplateOptions, itemId, itemState, savedState}, next) => Load2({
        _key: loadingProofs.value,
        fetch: async () => {
            let reproof = loadingProofs.value == 'reproof';
            if( masterTemplateOptions.options.find((o) => o.devKey === "non_user_customizable")?.value
                && !masterTemplateOptions?.components?.[componentTemplateId]?.requireManualProof
                && !isPickPack(componentState.value)
                && (componentTemplateOptions.proofEngineType == 0 || componentTemplateOptions.proofEngineType == 7 || componentTemplateOptions.proofEngineType == 8)
                && !(componentState.value?.itemThumbnails?.length > 0)
            )
            {
                //Need to update order data for reproof to use the new back.
                if( !equalDeep( savedState.value, itemState.value ) )
                {
                    await apis.next.item.updateItem(itemState.value);
                    savedState.onChange(itemState.value);
                }
                let results = await initiateProof( apis, itemId, componentTemplateId, reproof );
                if(results?.itemThumbnails?.length)
                {
                    componentState.onChange({...componentState.value, ...results});
                }
                loadingProofs.onChange(false);
            }
            else
            {
                loadingProofs.onChange(false);
            }
        },
        next
    })],
    ["itemOptions", ({componentTemplateOptions:templateOptions, componentTemplateId, item=null, itemComponentState, componentState}, next) => componentState.value.selections.quantity > 0 && ItemOptions({
        templateOptions,
        item: !item ? item : {
            ...clearLegacyItemData(item),
            result: item.result,
        },
        extraModules: [
            {
                name: "mailingListIds",
                load: ({}, next) => next(item?.selections?.mailingListIds?.map((ml) => ml.id) ?? []),
            },
            ...pickPackModules({item, state: itemComponentState, templateOptions, sequence: componentTemplateId}),
            ...yardSignModules({item, state: itemComponentState, templateOptions, sequence: componentTemplateId}),
        ],
        next,
    })],
    ["templateSelectionTemplateView", ({componentBack, componentState, componentTemplateId, componentTemplateOptions, loadingProofs, masterTemplateOptions, }, next) => (
        componentBack && TemplateSelectionTemplateView({
            allowReproof: masterTemplateOptions.options.find((o) => o.devKey === "non_user_customizable")?.value
                && !masterTemplateOptions?.components?.[componentTemplateId]?.requireManualProof
                && !isPickPack(componentState.value)
                && (componentTemplateOptions.proofEngineType == 0 || componentTemplateOptions.proofEngineType == 7 || componentTemplateOptions.proofEngineType == 8),
            loadingProofs,
            state: componentState,
            templateBack: componentBack,
            next,
        })
    )],
    ({
        componentTemplateOptions,
        componentTemplateId,
        componentBack,
        componentState,
        customizeStatus,
        item,
        itemComponentState,
        itemOptions,
        redirectAction,
        templateSelectionTemplateView,
    }) => (
        <>
            {cs(
                consumeContext("userLevels"),
                ["isInternalUser", ({userLevels}, next) => next(userLevels?.internalUser)],
                keyed(componentTemplateId),
                (_, next) => (
                    !customizeStatus.active ? (
                        next()
                    ) : (
                        <div className="center">
                            {customizeStatus.message}
                        </div>
                    )
                ),
                ({isInternalUser}) => (
                    <div className="card" id={`component-${componentTemplateId}`} xd-qa-type="card" xd-qa-name={componentTemplateId}>
                        <div className="content">
                            <div className="left">
                                {templateSelectionTemplateView?.render()}
                            </div>
                            <div className="right">
                                {ComponentEditor({
                                    componentState,
                                    item,
                                    itemComponentState,
                                    itemOptions,
                                    redirectAction,
                                    templateBack: componentBack,
                                    templateBackId: componentState?.value?.sku?.back,
                                    templateOptions: componentTemplateOptions,
                                    isInternalUser,
                                })}
                            </div>
                        </div>
                    </div>
                ),
            )}
        </>
    )
);

const clearLegacyItemData = (item) => (
    (!item || !isLegacyItem(item)) ? (
        item
    ) : {
        itemId: item.itemId,
        itemName: item.itemName,
    }
);

const itemRequiresAttention = (item) => {
    return oMapToArr(item)
        .filter((component) => component.selections?.quantity > 0)
        .some((component) => (componentRequiresAttention(component))
    )
}

const componentRequiresAttention = (component) => isPickPack(component) ? (
    component.selections?.options?.some((option) => option?.error)
) : (
    component.selections?.options?.some((option) => option?.error) || !(component?.itemThumbnails?.length > 0) || !!component?.changeBackOverride
);

const initiateProof = async ( apis, itemId, componentId, ignoreProofStats ) => {
    return await apis.next.initiateProofing( itemId, componentId, ignoreProofStats )
    .then(data => {
        if (!data['error']) {
            return data;
        } else {
            console.log("error", data['error']);
            return false;
        }
    });
}
